import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';

const NAV_SECTIONS = [
  {
    title: 'Getting Started',
    basePath: '/getting-started',
    items: [
      { label: 'Authentication', hash: '#authentication' },
      { label: 'Quick Start', hash: '#quick-start' },
    ],
  },
  {
    title: 'Evidence API',
    basePath: '/evidence-api',
    items: [
      { label: 'Upload', hash: '#upload' },
      { label: 'Query', hash: '#query' },
      { label: 'Chain of Custody', hash: '#chain-of-custody' },
    ],
  },
  {
    title: 'WORM Engine',
    basePath: '/worm-api',
    items: [
      { label: 'Write Once', hash: '#write-once' },
      { label: 'Verify', hash: '#verify' },
      { label: 'Audit Log', hash: '#audit-log' },
    ],
  },
  {
    title: 'Agent API',
    basePath: '/agent-api',
    items: [
      { label: 'Register', hash: '#register' },
      { label: 'Execute', hash: '#execute' },
      { label: 'Monitor', hash: '#monitor' },
    ],
  },
  {
    title: 'Security',
    basePath: '/getting-started',
    items: [
      { label: 'mTLS Setup', hash: '#mtls-setup' },
      { label: 'JWT Auth', hash: '#authentication' },
      { label: 'ABAC Policies', hash: '#abac-policies' },
    ],
  },
  {
    title: 'Webhooks',
    basePath: '/getting-started',
    items: [
      { label: 'Events', hash: '#webhook-events' },
      { label: 'Payloads', hash: '#webhook-payloads' },
      { label: 'Retry Policy', hash: '#retry-policy' },
    ],
  },
];

export default function DocSidebar() {
  const location = useLocation();
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    NAV_SECTIONS.forEach((s, i) => {
      initial[i] = location.pathname.startsWith(s.basePath);
    });
    initial[0] = true;
    return initial;
  });

  const toggle = (idx) => {
    setOpenSections((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <aside
      className="fixed top-0 left-0 h-screen z-30 flex flex-col"
      style={{
        width: '260px',
        background: 'var(--bg-secondary)',
        borderRight: '1px solid var(--border)',
      }}
    >
      {/* Logo */}
      <div className="px-5 py-5" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-deep))' }}
          >
            <span className="text-white font-heading font-bold text-sm">N</span>
          </div>
          <div>
            <h1 className="font-heading font-bold text-sm tracking-tight" style={{ color: 'var(--text-primary)' }}>
              NOYKA
            </h1>
            <span
              className="text-[10px] font-mono uppercase tracking-widest"
              style={{ color: 'var(--text-secondary)' }}
            >
              API Reference
            </span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-3">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
          style={{
            border: '1px solid var(--border)',
            background: 'var(--bg-card)',
            color: 'var(--text-muted)',
          }}
        >
          <Search className="w-4 h-4" />
          <span className="font-body">Search docs...</span>
          <kbd
            className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-mono"
            style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}
          >
            /
          </kbd>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
        {NAV_SECTIONS.map((section, idx) => (
          <div key={idx}>
            {/* Section header */}
            <button
              onClick={() => toggle(idx)}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-heading font-semibold uppercase tracking-wider transition-colors hover:opacity-80"
              style={{ color: 'var(--text-muted)' }}
            >
              <ChevronRight
                className={`w-3.5 h-3.5 transition-transform duration-200 ${openSections[idx] ? 'rotate-90' : ''}`}
              />
              {section.title}
            </button>

            {/* Section items */}
            {openSections[idx] && (
              <div className="ml-2 space-y-0.5 pb-2">
                {section.items.map((item) => {
                  const to = section.basePath + item.hash;
                  const isActive =
                    location.pathname === section.basePath &&
                    location.hash === item.hash;

                  return (
                    <NavLink
                      key={item.label}
                      to={to}
                      className={`sidebar-item ${isActive ? 'active' : ''}`}
                    >
                      <span
                        className="w-1 h-1 rounded-full bg-current opacity-40"
                      />
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer — API Status */}
      <div className="px-5 py-4" style={{ borderTop: '1px solid var(--border)' }}>
        <div className="flex items-center gap-2 text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
          <span
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ background: 'var(--accent-success)' }}
          />
          <span style={{ color: 'var(--text-secondary)' }}>API Operational</span>
        </div>
      </div>
    </aside>
  );
}
